import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { LEVELS, ROUTES, xpProgress } from "../lib/gameEngine";
import { getMemberProfile, listProvingMissions, submitProvingMission, updateRoute } from "../lib/memberService";
import { StatusPanel } from "../components/StatusPanel";
import type { MiddlemanRoute } from "../lib/types";

export function ProvingGroundPage() {
  const queryClient = useQueryClient();
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const profile = useQuery({ queryKey: ["profile"], queryFn: getMemberProfile });
  const missions = useQuery({
    queryKey: ["proving-missions"],
    queryFn: listProvingMissions,
    enabled: Boolean(profile.data && profile.data.activeRoute !== "unplaced"),
  });
  const chooseRoute = useMutation({
    mutationFn: (route: Exclude<MiddlemanRoute, "unplaced">) => updateRoute(route),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["profile"] }),
        queryClient.invalidateQueries({ queryKey: ["proving-missions"] }),
      ]);
    },
  });
  const submit = useMutation({
    mutationFn: ({ step, submission }: { step: number; submission: string }) => submitProvingMission(step, submission),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["proving-missions"] }),
  });

  if (profile.isLoading) return <main className="login-shell"><h1>Loading Proving Ground…</h1></main>;
  if (!profile.data) return <Navigate to="/login" replace />;
  const progress = xpProgress(profile.data.xp);

  return (
    <main className="standalone-shell">
      <header className="standalone-head">
        <Link className="brand" to="/app"><span className="brand-mark">H</span><span><b>THE HOUSE</b><small>PROVING GROUND</small></span></Link>
        <Link className="btn" to="/app">TASK BOARD</Link>
      </header>
      <section className="proving-intro">
        <span className="eyebrow">LEVEL 0 → 4</span>
        <h1>PROVE YOU CAN<br /><span>FOLLOW THE SOP.</span></h1>
        <p>Finish three practice tasks before you touch paid work. A Level 6+ Reviewer checks the live drill.</p>
        <div className="level-card">
          <div><span>LEVEL {profile.data.level}</span><strong>{progress.current.title}</strong></div>
          <div className="xp-track"><i style={{ width: `${progress.percent}%` }} /></div>
          <small>{profile.data.xp} XP · PAID WORK OPENS AT 1,200 XP</small>
        </div>
      </section>

      {profile.data.activeRoute === "unplaced" && (
        <section className="route-choice-panel" aria-labelledby="route-choice-title">
          <div className="section-head">
            <div><span className="eyebrow">CHOOSE YOUR TRACK</span><h2 id="route-choice-title">Pick the product-distribution work you want first.</h2></div>
            <p>Your route controls which SOPs, proof, and paid tasks you see.</p>
          </div>
          {chooseRoute.isError && <StatusPanel title="Route choice failed" message={chooseRoute.error.message} kind="error" />}
          <div className="route-grid compact">
            {ROUTES.map((route) => (
              <button className="route-card" disabled={chooseRoute.isPending} key={route.id} onClick={() => chooseRoute.mutate(route.id)}>
                <span className="route-icon">{route.icon}</span>
                <small>{route.moves}</small>
                <h2>{route.name}</h2>
                <p>{route.description}</p>
                <b>CHOOSE {route.name.toUpperCase()}</b>
              </button>
            ))}
          </div>
        </section>
      )}

      {profile.data.level >= 4 && <StatusPanel title="Paid tasks unlocked" message="All proving steps are approved. You can claim tasks from your route board." kind="success" />}
      {submit.isError && <StatusPanel title="Submission failed" message={submit.error.message} kind="error" />}
      {profile.data.activeRoute !== "unplaced" && (
        <section className="proving-grid">
          {(missions.data ?? []).map((mission) => {
            const locked = mission.status === "locked";
            const value = drafts[mission.step] ?? mission.submission ?? "";
            return (
              <article className={`proving-card ${locked ? "locked" : ""}`} key={mission.step}>
                <div className="proving-number">0{mission.step}</div>
                <span className={`tag ${mission.status === "approved" ? "green" : ""}`}>{mission.status}</span>
                <h2>{mission.title}</h2>
                <p>{mission.instructions}</p>
                <textarea className="field" aria-label={`${mission.title} submission`} disabled={locked || mission.status === "approved" || mission.status === "submitted"} value={value} onChange={(event) => setDrafts((current) => ({ ...current, [mission.step]: event.target.value }))} placeholder="Write your proof here. Add working links when the task asks for them." />
                <button className="btn primary" disabled={locked || mission.status !== "ready" || value.trim().length < 30 || submit.isPending} onClick={() => submit.mutate({ step: mission.step, submission: value })}>SUBMIT PROOF</button>
                <small>APPROVAL REWARD: +75 XP</small>
              </article>
            );
          })}
        </section>
      )}

      <section className="level-roadmap">
        <div className="section-head"><div><span className="eyebrow">THE LADDER</span><h2>What each level unlocks</h2></div></div>
        {LEVELS.map((level) => <div className={`level-row ${profile.data!.level >= level.level ? "reached" : ""}`} key={level.level}><span>L{level.level}</span><strong>{level.title}</strong><p>{level.unlock}</p><b>{level.xp.toLocaleString()} XP</b></div>)}
      </section>
    </main>
  );
}
